import { useState, useEffect } from 'react';
import { ContextModalProps } from '@mantine/modals';
import { Center, Checkbox, Group, SegmentedControl, Textarea, TextInput } from '@mantine/core';
import { DatePicker, isSameDate, TimeInput } from '@mantine/dates';
import { TransButtonPrimaryWithSpinner } from '../../../Atoms/Buttons';
import { createTask } from '../../../Service/restapi/taskService';
import AgendaType from '../../../Types/Enums/AgendaType';
import EventSearchAutocomplete from '../../../Organismns/Event/EventSearchAutocomplete';
import { ConstantS } from '../../../Service/ConstantS';
import { MixpanelS } from '../../../Service/MixpanelS';
import { ToastS } from '../../../Service/ToastS';
import type { useBookitupTeam } from '../../../Service/hooks/useBookitupTeam';
import CalendarDay from '../../../Atoms/inputs/CalendarDay';
import TaskApplicationUserSelect from './TaskApplicationUserSelect';
import TaskContactSelect from './TaskContactSelect';
import TaskGroupSelection from './TaskGroupSelection';

type Props = {
  entityId?: string;
  entityType?: string;
  initialContactId?: number;
  showEntityTypeSelection?: boolean;
  finish: () => void;
  profile: any;
  bookitupTeam: ReturnType<typeof useBookitupTeam>;
  teamAddonPresent: boolean;
};

const TaskCreateModal = ({ context, id, innerProps }: ContextModalProps<Props>) => {
  const {
    entityId,
    entityType,
    initialContactId,
    finish,
    showEntityTypeSelection,
    profile,
    bookitupTeam,
    teamAddonPresent,
  } = innerProps;

  const [name, setName] = useState('');
  const [agendaType, setAgendaType] = useState<AgendaType>(AgendaType.STRING);
  const [dueDate, setDueDate] = useState<Date | null>(null);
  const [groupName, setGroupName] = useState<string>();

  const [priority, setPriority] = useState(false);
  const [notes, setNotes] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [applicationUserId, setApplicationUserId] = useState<string | null>(null);
  const [contactId, setContactId] = useState<string | null>(initialContactId?.toString() ?? null);

  const [selectedEventId, setSelectedEventId] = useState(entityId ?? null);

  const [selectedEntityType, setSelectedEntityType] = useState(entityType);

  useEffect(() => {
    setName('');
    setGroupName('Standard');
    setDueDate(null);
    setNotes(undefined);
    setPriority(false);
    setApplicationUserId(null);
    setContactId(initialContactId?.toString() ?? null);
  }, [initialContactId, profile.id]);

  const create = () => {
    if (selectedEntityType === 'events' && (!name || !groupName || !selectedEventId)) {
      ToastS.error(
        'create.task',
        'Es konnte keine neue Aufgabe erstellt werden. Bitte Name, Gruppe und Veranstaltung auswählen.',
      );
    } else if (selectedEntityType === 'profiles' && (!name || !groupName)) {
      ToastS.error('create.task', 'Es konnte keine neue Aufgabe erstellt werden: Bitte Name und Gruppe auswählen.');
    } else {
      createAttr();
    }
  };

  function createAttr() {
    setLoading(true);
    createTask({
      name,
      agendaType,
      entityId: selectedEntityType === 'events' ? selectedEventId : profile.id,
      entityType: selectedEntityType,
      groupName,
      dueDate: dueDate ?? undefined,
      notes,
      priority,
      applicationUserId: applicationUserId ? Number(applicationUserId) : undefined,
      contactId: contactId ? Number(contactId) : undefined,
    })
      .then((resp) => {
        if (resp.ok) {
          finish();
          MixpanelS.track(ConstantS.TrackingEvents.TaskCreated);
          ToastS.success('create.task', 'Neue Aufgabe erstellt');
        } else {
          console.error(`could not create new task ${name} of agendaType ${agendaType}`);
          ToastS.error('create.task', 'Es konnte keine neue Aufgabe erstellt werden');
        }
      })
      .catch(() => {
        console.error(`could not create new custom task ${name} of agendaType ${agendaType}`);
        ToastS.error('create.task', 'Es konnte keine neue Aufgabe erstellt werden');
      })
      .finally(() => {
        setLoading(false);
      });
  }

  const renderDate = (date: Date) => {
    const selected = dueDate ? isSameDate(dueDate, date) : false;

    return <CalendarDay date={date} selected={selected} />;
  };

  const updateDueDate = (value: Date | null) => {
    if (!value) {
      setDueDate(null);
      return;
    }

    setDueDate(
      (current) =>
        new Date(
          value.getFullYear(),
          value.getMonth(),
          value.getDate(),
          current?.getHours() ?? 0,
          current?.getMinutes() ?? 0,
        ),
    );
  };

  const updateDueTime = (value: Date) => {
    setDueDate((current) =>
      current
        ? new Date(current.getFullYear(), current.getMonth(), current.getDate(), value.getHours(), value.getMinutes())
        : null,
    );
  };

  return (
    <>
      <div>
        {showEntityTypeSelection && (
          <Center>
            {' '}
            <SegmentedControl
              value={selectedEntityType}
              onChange={(value) => {
                setSelectedEntityType(value);
                setContactId(null);
              }}
              data={[
                { label: 'Event', value: 'events' },
                { label: 'Allgemein', value: 'profiles' },
              ]}
              sx={{ marginBottom: 20 }}
            />
          </Center>
        )}

        {!entityId && selectedEntityType === 'events' && (
          <div style={{ marginBottom: 10 }}>
            <EventSearchAutocomplete
              clearable
              clearEvent={() => {
                setSelectedEventId(null);
                setContactId(null);
              }}
              selectEvent={(e) => {
                setSelectedEventId(e.id.toString());
                setContactId(null);
              }}
            />
          </div>
        )}

        <TextInput
          onChange={(e: any) => {
            setName(e.target.value);
          }}
          value={name}
          description="Titel der Aufgabe"
          sx={{ marginBottom: 10 }}
        />

        <Group grow spacing={5} align="flex-start" sx={{ marginBottom: 10 }}>
          <DatePicker renderDay={renderDate} onChange={updateDueDate} value={dueDate} description="Erledigen bis" />
          <TimeInput disabled={!dueDate} format="24" onChange={updateDueTime} value={dueDate} description="Uhrzeit" />
        </Group>

        <Textarea
          value={notes}
          onChange={(e: any) => {
            setNotes(e.target.value);
          }}
          description="Beschreibung der Aufgabe"
          sx={{ marginBottom: 10 }}
          autosize
          minRows={5}
          maxRows={10}
        />
      </div>
      <div style={{ marginBottom: 10 }}>
        <TaskGroupSelection required clearable entityType={selectedEntityType} onGroupNameChanged={setGroupName} />
      </div>
      <TaskApplicationUserSelect
        value={applicationUserId}
        onChange={setApplicationUserId}
        bookitupTeam={bookitupTeam}
        profile={profile}
        teamAddonPresent={teamAddonPresent}
      />
      <TaskContactSelect
        entityType={selectedEntityType}
        eventId={selectedEventId}
        value={contactId}
        onChange={setContactId}
      />
      <div style={{ flex: 1, justifySelf: 'center', marginBottom: 20 }}>
        <Checkbox
          label="Als wichtige Aufgabe markieren"
          checked={priority}
          onChange={(e: any) => setPriority(e.currentTarget.checked)}
        />
      </div>

      <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'center' }}>
        <TransButtonPrimaryWithSpinner
          isLoading={loading}
          disabled={loading}
          onClickHandler={create}
          enabled={name}
          text="Aufgabe erstellen"
        />
      </div>
    </>
  );
};

export default TaskCreateModal;
